import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { useState } from "react";
import './EditHotelDrawer.css';

const EyeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 18l6-6-6-6"/>
  </svg>
);

const ChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M15 18l-6-6 6-6"/>
  </svg>
);

const LockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const AlertIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

function PasswordSection({ onBack }) {
  const [current, setCurrent]       = useState("");
  const [newPwd, setNewPwd]         = useState("");
  const [confirm, setConfirm]       = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess]       = useState(false);
  const [error, setError]           = useState("");

  const getStrength = (pwd) => {
    let s = 0;
    if (pwd.length >= 8) s++;
    if (/[A-Z]/.test(pwd)) s++;
    if (/[0-9]/.test(pwd)) s++;
    if (/[^A-Za-z0-9]/.test(pwd)) s++;
    return s;
  };

  const strengthMeta = [
    { label: "",          color: "transparent", pct: "0%"   },
    { label: "Faible",    color: "#E24B4A",      pct: "25%"  },
    { label: "Moyen",     color: "#EF9F27",      pct: "50%"  },
    { label: "Fort",      color: "#1D9E75",      pct: "75%"  },
    { label: "Très fort", color: "#0F6E56",      pct: "100%" },
  ];

  const strength = getStrength(newPwd);
  const meta = strengthMeta[strength];
  const passwordsMatch    = newPwd && confirm && newPwd === confirm;
  const passwordsMismatch = confirm && newPwd !== confirm;

const handleSave = async () => {
  setError("");
  if (!current)        { setError("Veuillez saisir votre mot de passe actuel."); return; }
  if (strength < 2)    { setError("Le nouveau mot de passe est trop faible."); return; }
  if (!passwordsMatch) { setError("Les mots de passe ne correspondent pas."); return; }

  try {
    const res = await fetch("https://api.komorotel.com/api/authAdmin/change-password", {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ currentPassword: current, newPwd }),
    });

    if (!res.ok) {
      const data = await res.json();
      setError(data.message || "Une erreur est survenue.");
      return;
    }

    setSuccess(true);
    setCurrent(""); setNewPwd(""); setConfirm("");
  } catch (err) {
    setError("Erreur réseau. Veuillez réessayer.");
  }
};

  return (
    <>
      <div className="drawer-header">
        <div className="dh-left">
          <button className="back-btn" onClick={onBack}>
            <ChevronLeft />
            Retour
          </button>
          <h3 className="drawer-title">Mot de passe</h3>
        </div>
        <span className="admin-badge">Administrateur</span>
      </div>

      <div className="drawer-body">

        {success && (
          <div className="alert-banner success">
            <CheckIcon />
            Mot de passe mis à jour avec succès.
          </div>
        )}

        {error && (
          <div className="alert-banner error">
            <AlertIcon />
            {error}
          </div>
        )}

        {/* Mot de passe actuel */}
        <div className="field-group">
          <label className="section-label">Mot de passe actuel</label>
          <div className="input-wrap">
            <input
              className="field-input"
              type={showCurrent ? "text" : "password"}
              placeholder="••••••••"
              value={current}
              onChange={(e) => { setCurrent(e.target.value); setError(""); setSuccess(false); }}
            />
            <button className="eye-btn" onClick={() => setShowCurrent(p => !p)}>
              {showCurrent ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
        </div>

        {/* Nouveau mot de passe */}
        <div className="field-group">
          <label className="section-label">Nouveau mot de passe</label>
          <div className="input-wrap">
            <input
              className="field-input"
              type={showNew ? "text" : "password"}
              placeholder="••••••••"
              value={newPwd}
              onChange={(e) => { setNewPwd(e.target.value); setError(""); setSuccess(false); }}
            />
            <button className="eye-btn" onClick={() => setShowNew(p => !p)}>
              {showNew ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          {newPwd.length > 0 && (
            <>
              <div className="strength-track">
                <div className="strength-bar" style={{ width: meta.pct, background: meta.color }} />
              </div>
              <span className="field-hint" style={{ color: meta.color }}>{meta.label}</span>
            </>
          )}
          {!newPwd && (
            <span className="field-hint">Au moins 8 caractères, une majuscule, un chiffre.</span>
          )}
        </div>

        {/* Confirmation */}
        <div className="field-group">
          <label className="section-label">Confirmer le nouveau mot de passe</label>
          <div className="input-wrap">
            <input
              className={`field-input ${passwordsMatch ? "input-match" : ""} ${passwordsMismatch ? "input-mismatch" : ""}`}
              type={showConfirm ? "text" : "password"}
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setError(""); }}
            />
            <button className="eye-btn" onClick={() => setShowConfirm(p => !p)}>
              {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          {passwordsMatch    && <span className="field-hint hint-ok">Les mots de passe correspondent.</span>}
          {passwordsMismatch && <span className="field-hint hint-err">Les mots de passe ne correspondent pas.</span>}
        </div>
      </div>

      <div className="drawer-footer">
        <button className="modern-button" onClick={handleSave}>
          Mettre à jour le mot de passe
        </button>
      </div>
    </>
  );
}

export default function EditHotelDrawer({ isOpen, onClose, data, onSave }) {
  const [formData, setFormData] = useState({
    island: data?.island || "",
    city:   data?.city   || "",
    adress: data?.adress || "",
    type:   data?.type   || "",
    desc:   data?.desc   || "",
    email:  data?.email  || "",
  });

  const [view, setView] = useState("hotel"); // "hotel" | "password"

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => setView("hotel"), 300);
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="drawer-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="drawer"
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          <AnimatePresence mode="wait">
            {view === "hotel" ? (
              <motion.div
                key="hotel"
                className="drawer-inner"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.18 }}
              >
                <div className="drawer-header">
                  <h3 className="drawer-title">Modifier l'hôtel</h3>
                  <button className="close-btn" onClick={handleClose}>✕</button>
                </div>

                <div className="drawer-body">

                  {/* ÎLE */}
                  <div className="field-group">
                    <label className="section-label">Île</label>
                    <div className="select-grid">
                      {["Ngazidja", "Moheli", "Anjouan"].map(option => (
                        <div
                          key={option}
                          className={`select-card ${formData.island === option ? "selected" : ""}`}
                          onClick={() => setFormData(prev => ({ ...prev, island: option }))}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* VILLE */}
                  <div className="field-group">
                    <label className="section-label">Ville</label>
                    <input className="field-input" name="city" value={formData.city} onChange={handleChange} placeholder="Ville" />
                  </div>

                  {/* ADRESSE */}
                  <div className="field-group">
                    <label className="section-label">Adresse</label>
                    <input className="field-input" name="adress" value={formData.adress} onChange={handleChange} placeholder="Adresse" />
                  </div>

                  {/* TYPE */}
                  <div className="field-group">
                    <label className="section-label">Type</label>
                    <div className="select-grid">
                      {["hotel", "chambre_hote", "appartement_studio"].map(option => (
                        <div
                          key={option}
                          className={`select-card ${formData.type === option ? "selected" : ""}`}
                          onClick={() => setFormData(prev => ({ ...prev, type: option }))}
                        >
                          {option === "hotel" && "Hôtel"}
                          {option === "chambre_hote" && "Chambre d'hôte"}
                          {option === "appartement_studio" && "Appartement & Studio"}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* EMAIL */}
                  <div className="field-group">
                    <label className="section-label">Email de contact</label>
                    <input className="field-input" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="contact@hotel.com" />
                    <p className="field-hint">Utilisé pour les notifications de réservation et d'annulation.</p>
                  </div>

                  {/* DESCRIPTION */}
                  <div className="field-group">
                    <label className="section-label">Description</label>
                    <textarea className="field-input field-textarea" name="desc" value={formData.desc} onChange={handleChange} placeholder="Description de l'établissement..." />
                  </div>

                  {/* SECTION MOT DE PASSE */}
                  <div className="section-divider" />
                  <button className="pwd-nav-item" onClick={() => setView("password")}>
                    <span className="pwd-nav-left">
                      <span className="pwd-nav-icon"><LockIcon /></span>
                      <span className="pwd-nav-text">
                        <span className="pwd-nav-title">Modifier le mot de passe</span>
                        <span className="pwd-nav-sub">Sécurité du compte administrateur</span>
                      </span>
                    </span>
                    <ChevronRight />
                  </button>

                </div>

                <div className="drawer-footer">
                  <button className="modern-button" onClick={() => onSave(formData)}>
                    Enregistrer les modifications
                  </button>
                </div>
              </motion.div>

            ) : (
              <motion.div
                key="password"
                className="drawer-inner"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.18 }}
              >
                <PasswordSection onBack={() => setView("hotel")} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}